// =============================================================================
// app/src/lib/carro.ts — o carro da venda num campo só (08/10/2026).
//
// ATÉ AQUI eram duas listas (montadora → modelo) com as VERSÕES da FIPE: 549
// opções só na VW, todas do tipo "Gol (novo) 1.0 Mi Total Flex 8V 4p". Em três
// semanas e meia, 42 de 737 vendas tiveram carro (5,7%) — e em 251 vendas o
// carro estava SÓ no texto da peça diversa ("LÂMPADA H4 GOL"). O vendedor não
// deixou de saber o carro; ele desistiu da lista.
//
// AGORA o vendedor digita como fala ("gol 2010 1.0") e a tela lê:
//
//   montadora  na grafia da FIPE ("VW - VolksWagen"), a mesma de
//              vehicle_models e de montadora_canonica() (092);
//   modelo     o NOME-BASE na frente + o que mais foi digitado ("Gol 1.0",
//              "Uno Mille", "Corsa Classic 1.0 16V"). Com o nome-base na
//              frente, modelo_base_de() (092) agrupa a venda no relatório;
//   ano        um número de 4 dígitos (ou 2: "gol 98" → 1998).
//
// O que continua igual — e é o que impede "VW", "Volks" e "Volkswagen" de
// virarem três carros: a MONTADORA nunca é texto livre. Ou ela vem do modelo
// reconhecido, ou de uma palavra que É montadora, ou de um botão. Sem ela, o
// carro não vai ao banco (carroPreenchido), como sempre foi.
//
// Tudo aqui é função PURA: os testes do app rodam em node, sem navegador, e é
// isto que se testa. A tela (componentes/SeletorVeiculo.tsx) só desenha.
// =============================================================================

import type { VehicleCatalog, VehicleModel } from '@core/types';

/** As colunas que a tela lê de vehicle_models e de vehicle_catalog. Lista em
 *  vez de string solta no select (como COLUNAS_DA_LISTA, contasPagar.ts): o
 *  TypeScript confere cada nome contra core/types.ts, e coluna com nome errado
 *  derruba a consulta só no navegador do vendedor. */
export const COLUNAS_DO_MODELO_BASE = [
  'id', 'montadora', 'nome', 'slug', 'apelidos',
] as const satisfies ReadonlyArray<keyof VehicleModel>;

export const COLUNAS_DA_FAMILIA = [
  'montadora', 'modelo',
] as const satisfies ReadonlyArray<keyof VehicleCatalog>;

// --- O valor do campo ---------------------------------------------------------

export interface CarroEscolhido {
  montadora: string;
  modelo: string;
  ano: string;
  veiculoId: string | null;
  /**
   * O que o vendedor DIGITOU (08/10/2026). É estado do usuário: dado que chega
   * tarde (a lista de modelos, as famílias da FIPE) melhora a LEITURA, nunca
   * reescreve isto — a trava de "tocado" do CLAUDE.md.
   *
   * `undefined` quer dizer "o carro veio pronto": venda antiga na correção,
   * rascunho de antes desta versão. O campo então mostra o carro remontado
   * (textoDoCampo) e NADA é relido até o vendedor mexer — é o que impede a
   * correção da observação de uma venda de setembro de reescrever a versão
   * FIPE que ela guardou.
   */
  texto?: string;
}

export const CARRO_VAZIO: CarroEscolhido = {
  montadora: '', modelo: '', ano: '', veiculoId: null, texto: '',
};

/** Tem carro de verdade? Montadora e modelo andam juntos — o banco recusa meio
 *  carro, e a tela não deve nem deixar chegar lá. */
export function carroPreenchido(c: CarroEscolhido): boolean {
  return c.montadora.trim() !== '' && c.modelo.trim() !== '';
}

/** "VW - VolksWagen" → "VolksWagen"; "Fiat" → "Fiat". A FIPE carimba a sigla
 *  antes do nome em algumas montadoras, e a sigla não ajuda quem lê o recibo. */
export function nomeDaMontadora(montadora: string): string {
  const partes = montadora.split(' - ');
  return (partes.length > 1 ? partes[partes.length - 1]! : montadora).trim();
}

/** Como o carro aparece para gente ler: sem o prefixo de sigla da FIPE. O
 *  separador é o espaço na frase ("VolksWagen Gol 1.0 2010") e o ponto no meio
 *  na linha de leitura da tela ("VolksWagen · Gol 1.0 · 2010"). */
export function textoDoCarro(c: {
  montadora: string | null; modelo: string | null; ano?: number | string | null;
}, separador = ' '): string {
  if (!c.montadora || !c.modelo) return '';
  return [nomeDaMontadora(c.montadora), c.modelo, c.ano ? String(c.ano) : null]
    .filter(Boolean).join(separador);
}

/** O que o campo mostra: o que foi digitado, ou — carro que veio pronto — o
 *  carro remontado em frase. Meio carro remonta só o que tem. */
export function textoDoCampo(c: CarroEscolhido): string {
  if (c.texto !== undefined) return c.texto;
  if (carroPreenchido(c)) return textoDoCarro(c);
  return [c.montadora && nomeDaMontadora(c.montadora), c.modelo, c.ano]
    .filter(Boolean).join(' ');
}

/** O carro como as RPCs o recebem (registrar_venda, editar_venda). Meio carro
 *  não vai: o banco recusa, e aqui nem chega a sair. O ano só acompanha
 *  quando há carro — ano solto não diz nada. Montadora e modelo saem COMO
 *  ESTÃO: a correção de uma venda antiga reenvia a versão FIPE byte a byte. */
export function carroParaRpc(c: CarroEscolhido): {
  veiculoMontadora: string | null;
  veiculoModelo: string | null;
  veiculoAno: number | null;
  veiculoId: string | null;
} {
  if (!carroPreenchido(c)) {
    return { veiculoMontadora: null, veiculoModelo: null, veiculoAno: null, veiculoId: null };
  }
  const ano = c.ano.trim() === '' ? NaN : Number(c.ano);
  return {
    veiculoMontadora: c.montadora,
    veiculoModelo: c.modelo,
    veiculoAno: Number.isInteger(ano) ? ano : null,
    veiculoId: c.veiculoId,
  };
}

/** O carro que uma venda JÁ TEM, para a correção (Vendas → Corrigir). Sem
 *  `texto` de propósito: ver CarroEscolhido.texto. */
export function carroDaVenda(v: {
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  veiculo_ano: number | null;
  customer_vehicle_id: string | null;
}): CarroEscolhido {
  return {
    montadora: v.veiculo_montadora ?? '',
    modelo: v.veiculo_modelo ?? '',
    ano: v.veiculo_ano ? String(v.veiculo_ano) : '',
    veiculoId: v.customer_vehicle_id ?? null,
  };
}

/** O carro que volta do rascunho (lib/rascunhoVenda.ts). O rascunho anterior
 *  a 08/10/2026 não tem `carroTexto`: volta pelas partes, e o campo remonta a
 *  frase com textoDoCarro. Nulo quando o rascunho não tinha carro. */
export function carroDoRascunho(r: {
  carroMontadora?: string;
  carroModelo?: string;
  carroAno?: string;
  carroVeiculoId?: string | null;
  carroTexto?: string;
}): CarroEscolhido | null {
  if (!r.carroModelo && !r.carroMontadora && !r.carroTexto?.trim()) return null;
  return {
    montadora: r.carroMontadora ?? '',
    modelo: r.carroModelo ?? '',
    ano: r.carroAno ?? '',
    veiculoId: r.carroVeiculoId ?? null,
    ...(r.carroTexto !== undefined ? { texto: r.carroTexto } : {}),
  };
}

// --- A base com que se lê o texto ---------------------------------------------

/** O modelo-base (vehicle_models, 092): "Gol", e não as 100 versões. */
export type ModeloBase = Pick<VehicleModel, 'montadora' | 'nome' | 'apelidos'>
  & Partial<Pick<VehicleModel, 'id' | 'slug'>>;

/** Uma "família" da FIPE: a primeira palavra do modelo ("Kombi", "Fusca",
 *  "147"). É o que resolve o que vehicle_models ainda não tem — Fusca e Kombi
 *  são o 3º e o 4º carro mais citado no balcão e não estão lá. */
export interface Familia {
  montadora: string;
  nome: string;
  /** Quantas versões da FIPE desta montadora começam por esta palavra. Desempata
   *  "147": Fiat (3) ganha de Alfa Romeo (1). */
  linhas: number;
  /** As segundas palavras dessas versões (normalizadas): desempatam "grand"
   *  quando o vendedor digitou "grand cherokee". */
  segundas: string[];
}

export interface BaseDeCarros {
  modelos: readonly ModeloBase[];
  /** As montadoras da FIPE (montadoras_catalogo, 107 hoje). */
  montadoras: readonly string[];
  familias: readonly Familia[];
  /**
   * Até onde a FIPE já respondeu — é o que diz se uma palavra desconhecida
   * "não é carro da FIPE" ou só "ainda não se sabe".
   *   'toda'      as famílias são a FIPE inteira (os testes);
   *   uma lista   os prefixos que a FIPE respondeu POR INTEIRO (a tela, que
   *               pergunta palavra a palavra: "pampa" respondida cobre
   *               "pampa"; uma resposta cortada no limite não entra);
   *   ausente     nenhum. Palavra desconhecida pode ser da FIPE, e a leitura
   *               PARA nela em vez de passar por cima: sem saber se "pampa" é
   *               carro, o "up" de "pampa pick up" não leva a venda para o up!.
   */
  fipe?: 'toda' | readonly string[];
}

/** O que a FIPE respondeu às palavras que a tela perguntou
 *  (consultas.familiasDoCatalogo). */
export interface RespostaDaFipe {
  /** Os prefixos respondidos por inteiro. */
  respondidas: readonly string[];
  familias: readonly Familia[];
}

/** A base da tela: a de vehicle_models mais o que a FIPE respondeu até agora. */
export function comFipe(base: BaseDeCarros, resposta: RespostaDaFipe | null | undefined): BaseDeCarros {
  return { ...base, familias: resposta?.familias ?? [], fipe: resposta?.respondidas ?? [] };
}

export const BASE_VAZIA: BaseDeCarros = { modelos: [], montadoras: [], familias: [] };

/** Os botões de "de que montadora?", na grafia da FIPE. Também entram sempre
 *  como montadora conhecida: se a lista do banco não chegar (rede), "vw gol"
 *  continua sendo VolksWagen. */
export const MONTADORAS_DOS_BOTOES = [
  'VW - VolksWagen', 'Fiat', 'GM - Chevrolet', 'Ford', 'Renault', 'Hyundai',
] as const;

/** Jeitos de escrever a montadora que não saem do nome da FIPE. "vw",
 *  "volkswagen", "gm" e "chevrolet" já saem dos dois lados do " - "; "citroen"
 *  sai da normalização (sem acento). */
const APELIDOS_DE_MONTADORA: Record<string, string> = {
  volks: 'VW - VolksWagen',
  wolks: 'VW - VolksWagen',
  chevy: 'GM - Chevrolet',
  mercedes: 'Mercedes-Benz',
  kia: 'Kia Motors',
};

/** Apelidos que, soltos num texto de peça, dizem mais outra coisa que o carro:
 *  "PICK UP", "TAPETE SPACE". Ficam fora da detecção no texto (carrosNoTexto);
 *  digitados no campo do carro continuam valendo. */
const AMBIGUOS_NO_TEXTO = new Set(['up', 'space']);

const LIMITE_DO_MODELO = 60;

// --- Normalização ---------------------------------------------------------------

/** Minúsculas, sem acento, vírgula decimal virando ponto ("1,0" → "1.0") e
 *  espaços colapsados. */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\s+/g, ' ').trim();
}

/** A palavra reduzida ao que importa para comparar: só letra e número.
 *  "UP!" e "up" são a mesma; "F-1000" e "f1000" também. */
function chave(s: string): string {
  return normalizar(s).replace(/[^a-z0-9]/g, '');
}

/** "1.0", "1,6", "2.0", "16v", "8V", "ap" (o motor AP da VW) e a cilindrada
 *  de 4 dígitos ("1600", "1300") — motor. Nunca é o modelo. "2000" tem cara
 *  de ano, e quem pergunta pelo ano antes (procurarModelo) decide. */
function ehMotor(t: string): boolean {
  return /^\d+[.,]\d+$/.test(t) || /^\d+v$/i.test(t) || /^ap$/i.test(t)
    || /^[1-9]\d00$/.test(t);
}

/**
 * Palavras que DESCREVEM o carro e nunca são o modelo: "novo gol", "nova
 * saveiro", "cross fox", "g5 gol", "weekend palio", "kombi pick up". Na procura
 * do modelo são puladas; no que se grava, ficam ("Gol Novo", "Fox Cross",
 * "Gol G5", "Palio Weekend"). "cross" e "novo" estão aqui também porque podem
 * ser família da FIPE (o Cross up!) ou começo de apelido (NOVO UNO): sem a
 * lista, "cross fox" seria um "Cross" da FIPE e "novo gol" só sugeria o Uno.
 * Apelido de vehicle_models que COMEÇA numa delas ("novo uno") vale antes.
 */
const DESCRITIVAS = new Set(['novo', 'nova', 'cross', 'weekend', 'pickup']);
/** Geração: "g" e um dígito ("g5 gol", "g4"). */
const GERACAO = /^g\d$/;
/** "pick up" é a carroceria — e não o up!. */
const PARES_DESCRITIVOS: ReadonlyArray<readonly string[]> = [['pick', 'up']];

/** Quantas palavras descritivas começam em `k` (0, 1, ou 2 no par). */
function descritivaEm(palavras: readonly Palavra[], k: number): number {
  const par = PARES_DESCRITIVOS.find((p) => casaEm(palavras, k, p));
  if (par) return par.length;
  const c = palavras[k]?.chave ?? '';
  return DESCRITIVAS.has(c) || GERACAO.test(c) ? 1 : 0;
}

/** Como a palavra digitada entra no modelo: "16v" → "16V", "g5" → "G5",
 *  "ap" → "AP", "classic" → "Classic", "TURBO" → "Turbo"; "GTI" e
 *  "SpaceFox" ficam. */
function formatarPalavra(original: string): string {
  const t = original.replace(/(\d),(\d)/g, '$1.$2');
  if (/^\d+v$/i.test(t)) return `${t.slice(0, -1)}V`;
  if (/^ap$/i.test(t)) return 'AP';
  if (/^[a-z]\d+[a-z]?$/i.test(t)) return t.toUpperCase();
  if (/^\p{L}+$/u.test(t)) {
    if (t === t.toLowerCase()) return t.charAt(0).toUpperCase() + t.slice(1);
    if (t === t.toUpperCase() && t.length > 3) {
      return t.charAt(0) + t.slice(1).toLowerCase();
    }
  }
  return t;
}

/** Corta no fim de palavra, sem passar do limite (o banco não limita; o
 *  relatório e o recibo agradecem). */
function limitar(s: string, max = LIMITE_DO_MODELO): string {
  if (s.length <= max) return s;
  const corte = s.slice(0, max);
  const espaco = corte.lastIndexOf(' ');
  return (espaco > 0 ? corte.slice(0, espaco) : corte).trim();
}

// --- As palavras do texto -------------------------------------------------------

interface Palavra {
  /** Como foi digitada (com a vírgula do motor já trocada por ponto). */
  original: string;
  /** Normalizada (minúscula, sem acento). */
  norm: string;
  chave: string;
  /** Posição entre as palavras do texto separadas por espaço. */
  indice: number;
}

function palavrasDe(texto: string): Palavra[] {
  return texto.trim().split(/\s+/)
    .map((original, indice) => {
      const o = original.replace(/(\d),(\d)/g, '$1.$2');
      const norm = normalizar(o);
      return { original: o, norm, chave: chave(o), indice };
    })
    // "-" ou "/" soltos não dizem nada.
    .filter((p) => p.chave !== '');
}

function tokensDe(s: string): string[] {
  return normalizar(s).split(' ').map(chave).filter(Boolean);
}

/** As palavras `alvo` aparecem a partir de `de`? Compara pela chave. */
function casaEm(palavras: readonly Palavra[], de: number, alvo: readonly string[]): boolean {
  if (alvo.length === 0 || de + alvo.length > palavras.length) return false;
  return alvo.every((t, k) => palavras[de + k]!.chave === t);
}

// --- A montadora ------------------------------------------------------------------

interface ApelidoDeMontadora { tokens: string[]; montadora: string }

function todasAsMontadoras(base: BaseDeCarros): string[] {
  const vistas = new Map<string, string>();
  const juntar = (m: string) => {
    const k = m.trim().toLowerCase();
    if (k && !vistas.has(k)) vistas.set(k, m.trim());
  };
  MONTADORAS_DOS_BOTOES.forEach(juntar);
  base.montadoras.forEach(juntar);
  base.modelos.forEach((m) => juntar(m.montadora));
  base.familias.forEach((f) => juntar(f.montadora));
  return [...vistas.values()];
}

function apelidosDeMontadora(base: BaseDeCarros): ApelidoDeMontadora[] {
  const porChave = new Map<string, ApelidoDeMontadora>();
  const juntar = (forma: string, montadora: string) => {
    const tokens = tokensDe(forma);
    const k = tokens.join(' ');
    // O primeiro ganha: as montadoras dos botões vêm antes de tudo.
    if (tokens.length > 0 && !porChave.has(k)) porChave.set(k, { tokens, montadora });
  };
  const conhecidas = todasAsMontadoras(base);
  for (const m of conhecidas) {
    const partes = m.split(' - ').map((p) => p.trim()).filter(Boolean);
    for (const p of partes.length > 1 ? partes : [m]) {
      juntar(p, m);
      // "Mercedes-Benz" também se escreve "mercedes benz".
      if (p.includes('-')) juntar(p.replace(/-/g, ' '), m);
    }
  }
  const porNome = new Map(conhecidas.map((m) => [m.toLowerCase(), m]));
  for (const [apelido, m] of Object.entries(APELIDOS_DE_MONTADORA)) {
    const alvo = porNome.get(m.toLowerCase());
    if (alvo) juntar(apelido, alvo);
  }
  // O mais comprido primeiro: "land rover" antes de "rover".
  return [...porChave.values()].sort((a, b) => b.tokens.length - a.tokens.length);
}

/** A palavra (ou palavras) que é montadora, e o resto do texto. Só UMA
 *  montadora sai: "dodge ram" é Dodge com modelo Ram. */
function separarMontadora(palavras: readonly Palavra[], base: BaseDeCarros): {
  montadora: string | null; resto: Palavra[];
} {
  const apelidos = apelidosDeMontadora(base);
  for (let i = 0; i < palavras.length; i += 1) {
    const achado = apelidos.find((a) => casaEm(palavras, i, a.tokens));
    if (achado) {
      return {
        montadora: achado.montadora,
        resto: palavras.filter((_, k) => k < i || k >= i + achado.tokens.length),
      };
    }
  }
  return { montadora: null, resto: [...palavras] };
}

const mesmaMontadora = (a: string, b: string | null) =>
  b === null || a.trim().toLowerCase() === b.trim().toLowerCase();

// --- O modelo ---------------------------------------------------------------------

/** O nome que vai para o banco quando o vendedor escreveu um APELIDO: o
 *  nome-base na frente, sempre ("mille" → "Uno Mille", nunca "Mille"). É o
 *  que deixa modelo_base_de() casar pelo NOME mesmo se os apelidos mudarem. */
function nomeComApelido(m: ModeloBase, apelido: string): string {
  const a = chave(apelido);
  const n = chave(m.nome);
  // "space fox", "hb 20", "up", "doblo": é o próprio nome escrito de outro jeito.
  if (!a || a === n || n.startsWith(a)) return m.nome;
  // "hb20s": o nome com uma letra a mais.
  if (a.startsWith(n)) {
    const sufixo = a.slice(n.length);
    return m.nome + (m.nome === m.nome.toUpperCase() ? sufixo.toUpperCase() : sufixo);
  }
  // "novo uno" já contém "uno": entra só o que falta.
  const doNome = new Set(tokensDe(m.nome));
  const extras = normalizar(apelido).split(' ').filter((p) => !doNome.has(chave(p)));
  return [m.nome, ...extras.map(formatarPalavra)].join(' ');
}

interface ModeloAchado {
  modelo: ModeloBase;
  apelido: string;
  de: number;
  ate: number;
}

/** A palavra pode ir à FIPE? Com 3 letras ou mais; número, já com 2 ("19",
 *  "21": o Renault 19 e o 21). Com menos, a lista nem caberia — e a palavra
 *  que não pode ir conta como respondida: a tela nunca saberia mais dela. */
export function podePerguntarAFipe(busca: string): boolean {
  return busca.length >= 3 || /^\d{2}$/.test(busca);
}

/** A FIPE já disse se esta palavra é família? Ver BaseDeCarros.fipe. */
function respondida(p: Palavra, base: BaseDeCarros): boolean {
  if (base.fipe === 'toda') return true;
  const busca = palavraParaBusca(p.norm);
  if (!podePerguntarAFipe(busca)) return true;
  return (base.fipe ?? []).some((r) => r !== '' && busca.startsWith(r));
}

interface Procura {
  /** O modelo de vehicle_models que começa na palavra do modelo. */
  modelo: ModeloAchado | null;
  /** Não havendo, a família da FIPE dessa palavra. */
  familia: Familia | null;
  /**
   * A palavra do modelo: a do achado; sem achado, a palavra em que a leitura
   * parou esperando a FIPE, ou a primeira que podia ser modelo. É ela que a
   * sugestão troca. -1 quando não há nenhuma ("fiat", "1.0").
   */
  k: number;
  /** As palavras que a FIPE ainda precisa responder — até o primeiro modelo
   *  de vehicle_models, que nenhuma resposta da FIPE desbanca. */
  perguntar: number[];
}

/**
 * ONDE está o modelo entre as palavras que sobraram da montadora: a PRIMEIRA,
 * da esquerda para a direita, que é carro conhecido — um apelido de
 * vehicle_models que COMEÇA nela ou uma família da FIPE dela. No caminho:
 *  - motor ("1.0", "16v", "ap", "1600") e descritiva ("novo", "cross", "g5",
 *    "weekend", "pick up") são puladas;
 *  - número com cara de ano é o ANO ("2010 gol", "2008 gol"), salvo quando
 *    disputa o modelo — com a montadora digitada ("peugeot 2008 griffe" é o
 *    2008) ou sem outra palavra que possa ser o modelo ("2008") — E é
 *    carro conhecido dessa montadora;
 *  - palavra desconhecida que a FIPE já disse não ser família é pulada
 *    ("quadrado gol" é o Gol). A que a FIPE ainda não respondeu PARA a
 *    leitura: "pampa pick up" não vira up! enquanto não se sabe da Pampa.
 *
 * Até a 2ª revisão do PR #271 o modelo era "só a primeira palavra", e uma
 * descritiva na frente ("novo gol", "cross fox", "g5 gol") deixava o carro sem
 * montadora. Antes disso (1ª revisão), apelido em QUALQUER posição ganhava, e
 * o do meio puxava o carro ("pampa pick up" gravava VolksWagen up!).
 *
 * A leitura (entenderCarro), a palavra que a sugestão troca (localizarPalavra)
 * e as palavras que a tela pergunta à FIPE (palavrasParaFipe) saem DAQUI.
 */
function procurarModelo(
  palavras: readonly Palavra[], base: BaseDeCarros, montadora: string | null, anoAtual: number,
): Procura {
  const ehAno = (p: Palavra) => anoDe(p.original, anoAtual) !== null;
  // As descritivas, para saber se sobra alguma palavra além dos anos.
  const descritiva = new Array<boolean>(palavras.length).fill(false);
  for (let k = 0; k < palavras.length;) {
    const d = descritivaEm(palavras, k);
    for (let i = 0; i < d; i += 1) descritiva[k + i] = true;
    k += Math.max(d, 1);
  }
  const anoDisputa = montadora !== null
    || palavras.every((p, k) => ehAno(p) || ehMotor(p.original) || descritiva[k]);

  // Mesmo com a montadora digitada, o número com cara de ano só disputa o
  // modelo quando NENHUMA palavra depois dele é carro conhecido: "renault 19
  // sandero" é o Sandero de 2019, não o Renault 19 (3ª conferência do #271);
  // "peugeot 2008 griffe" segue o 2008, porque "griffe" não é carro.
  const carroDepois = (k: number) => palavras.some((q, j) => j > k && !ehAno(q) && !ehMotor(q.original)
    && (acharModelo(palavras, base, montadora, j) !== null
      || acharFamilia(palavras, base, montadora, j) !== null));

  let achado: { modelo: ModeloAchado | null; familia: Familia | null; k: number } | null = null;
  let parada = -1;
  let desconhecida = -1;
  let anoCandidato = -1;
  const perguntar: number[] = [];
  for (let k = 0; k < palavras.length;) {
    const p = palavras[k]!;
    const ano = ehAno(p);
    if ((ano && (!anoDisputa || carroDepois(k))) || (!ano && ehMotor(p.original))) { k += 1; continue; }

    const modelo = acharModelo(palavras, base, montadora, k);
    if (modelo) {
      if (parada < 0) achado = { modelo, familia: null, k };
      break;
    }
    const d = ano ? 0 : descritivaEm(palavras, k);
    if (d > 0) { k += d; continue; }
    const familia = acharFamilia(palavras, base, montadora, k);
    if (familia) {
      if (parada < 0) achado = { modelo: null, familia, k };
      break;
    }

    if (ano) { if (anoCandidato < 0) anoCandidato = k; } else if (desconhecida < 0) desconhecida = k;
    if (!respondida(p, base)) {
      perguntar.push(k);
      // O número com cara de ano que a FIPE não respondeu fica sendo o ano
      // ("vw 2010 gol" é o Gol já); só a PALAVRA desconhecida para a leitura.
      if (!ano && parada < 0) parada = k;
    }
    k += 1;
  }

  const reserva = [parada, desconhecida, anoCandidato, descritiva.indexOf(true)]
    .find((i) => i >= 0) ?? -1;
  return {
    modelo: achado?.modelo ?? null,
    familia: achado?.familia ?? null,
    k: achado ? achado.k : reserva,
    perguntar,
  };
}

/**
 * O apelido (ou nome) mais comprido de vehicle_models que COMEÇA na palavra
 * `k` — e só ali. Apelido no meio de outro nome é o resto do modelo, nunca o
 * modelo: "kombi pick up" não é up!, "monza classic" não é Corsa.
 */
function acharModelo(
  palavras: readonly Palavra[], base: BaseDeCarros, montadora: string | null, k: number,
): ModeloAchado | null {
  if (k < 0) return null;
  const c = base.modelos
    .filter((m) => mesmaMontadora(m.montadora, montadora))
    .flatMap((m) => [m.nome, ...m.apelidos].map((apelido) => ({
      modelo: m, apelido, tokens: tokensDe(apelido),
    })))
    .filter((x) => x.tokens.length > 0)
    // O mais comprido primeiro: "palio weekend" ganha de "palio".
    .sort((a, b) => b.tokens.length - a.tokens.length)
    .find((x) => casaEm(palavras, k, x.tokens));
  return c ? { modelo: c.modelo, apelido: c.apelido, de: k, ate: k + c.tokens.length } : null;
}

/** A família da FIPE da palavra `k`, pela palavra exata. Empate entre
 *  montadoras: a que tem a palavra seguinte do texto entre as segundas
 *  palavras; depois, a que tem mais versões. */
function acharFamilia(
  palavras: readonly Palavra[], base: BaseDeCarros, montadora: string | null, k: number,
): Familia | null {
  const p = palavras[k];
  if (!p) return null;
  const iguais = base.familias
    .filter((f) => mesmaMontadora(f.montadora, montadora) && chave(f.nome) === p.chave);
  if (iguais.length === 0) return null;
  const seguinte = palavras[k + 1]?.chave;
  return [...iguais].sort((a, b) => {
    const sa = seguinte && a.segundas.includes(seguinte) ? 1 : 0;
    const sb = seguinte && b.segundas.includes(seguinte) ? 1 : 0;
    return sb - sa || b.linhas - a.linhas;
  })[0]!;
}

// --- O ano --------------------------------------------------------------------------

function anoDe(t: string, anoAtual: number): number | null {
  // "2010/2011" (fabricação/modelo): vale o do modelo, como no documento.
  const barra = /^(\d{2}|\d{4})\/(\d{2}|\d{4})$/.exec(t);
  if (barra) return anoDe(barra[2]!, anoAtual);
  if (/^\d{4}$/.test(t)) {
    const n = Number(t);
    return n >= 1950 && n <= anoAtual + 1 ? n : null;
  }
  if (/^\d{2}$/.test(t)) {
    const yy = Number(t);
    if (2000 + yy <= anoAtual + 1) return 2000 + yy;
    if (1900 + yy >= 1950) return 1900 + yy;
  }
  return null;
}

/** O ano entre as palavras que sobraram: o primeiro de 4 dígitos; não
 *  havendo, o primeiro de 2. Devolve o índice para a palavra sair do modelo. */
function separarAno(palavras: readonly Palavra[], anoAtual: number): {
  ano: number | null; resto: Palavra[];
} {
  const quatro = palavras.findIndex((p) => /^\d{4}$|\//.test(p.original) && anoDe(p.original, anoAtual) !== null);
  const dois = palavras.findIndex((p) => /^\d{2}$/.test(p.original) && anoDe(p.original, anoAtual) !== null);
  const k = quatro >= 0 ? quatro : dois;
  if (k < 0) return { ano: null, resto: [...palavras] };
  return { ano: anoDe(palavras[k]!.original, anoAtual), resto: palavras.filter((_, i) => i !== k) };
}

// --- A leitura ------------------------------------------------------------------------

export type FonteDoCarro = 'modelo' | 'fipe' | 'livre' | 'vazio';

export interface CarroLido {
  montadora: string | null;
  modelo: string | null;
  ano: number | null;
  /** De onde veio o modelo: vehicle_models, família da FIPE, ou o que foi
   *  digitado, sem reconhecer. */
  fonte: FonteDoCarro;
}

export interface OpcoesDaLeitura {
  /** A linha da venda (sale_items) não tem ano: lá o número fica no modelo. */
  comAno?: boolean;
  /** Para teste: o ano de hoje decide até onde vai "o ano que vem". */
  anoAtual?: number;
}

/**
 * Lê o que o vendedor digitou. Nesta ordem:
 *  (a) normaliza (minúscula, sem acento, "1,0" → "1.0");
 *  (b) a palavra que é montadora sai do texto e vira a grafia da FIPE;
 *  (c) MODELO PRIMEIRO: a primeira palavra, da esquerda para a direita, que é
 *      carro conhecido (procurarModelo) — vehicle_models pelo apelido mais
 *      comprido que COMEÇA nela, ou a família da FIPE dela. Motor, ano e
 *      descritiva ("novo", "cross", "g5", "pick up") ficam no caminho sem
 *      ganhar: "novo gol" é o Gol, "pampa pick up" é a Pampa. Nenhuma
 *      conhecida: o que foi digitado, com maiúscula inicial (fonte 'livre');
 *  (d) ANO depois do modelo — por isso "peugeot 2008 2018" é o 2008 de 2018;
 *  (e) o motor fica no modelo ("1.0", "16V");
 *  (f) modelo = nome-base + o resto digitado, até ~60 caracteres ("weekend
 *      palio" → "Palio Weekend", "g5 gol" → "Gol G5").
 */
export function entenderCarro(
  texto: string, base: BaseDeCarros, opcoes: OpcoesDaLeitura = {},
): CarroLido {
  const comAno = opcoes.comAno ?? true;
  const anoAtual = opcoes.anoAtual ?? new Date().getFullYear();
  const palavras = palavrasDe(texto ?? '');
  if (palavras.length === 0) return { montadora: null, modelo: null, ano: null, fonte: 'vazio' };

  const { montadora: digitada, resto } = separarMontadora(palavras, base);

  let montadora = digitada;
  let nome: string | null = null;
  let fonte: FonteDoCarro = 'livre';
  let sobra = resto;

  const { modelo, familia, k } = procurarModelo(resto, base, digitada, anoAtual);
  if (modelo) {
    montadora = modelo.modelo.montadora;
    nome = nomeComApelido(modelo.modelo, modelo.apelido);
    fonte = 'modelo';
    const repetidas = new Set([...tokensDe(modelo.modelo.nome), chave(modelo.apelido)]);
    // "classic corsa" não vira "Corsa Classic Corsa".
    sobra = resto.filter((_, i) => i < modelo.de || i >= modelo.ate)
      .filter((p) => !repetidas.has(p.chave));
  } else if (familia) {
    montadora = familia.montadora;
    nome = familia.nome;
    fonte = 'fipe';
    sobra = resto.filter((_, i) => i !== k);
  }

  let ano: number | null = null;
  if (comAno) {
    const separado = separarAno(sobra, anoAtual);
    ano = separado.ano;
    sobra = separado.resto;
  }

  const partes = [nome, ...sobra.map((p) => formatarPalavra(p.original))].filter(Boolean);
  const texto60 = partes.length > 0 ? limitar(partes.join(' ')) : null;
  return { montadora, modelo: texto60, ano, fonte };
}

/** O carro lido, já no formato do campo (strings, como CarroEscolhido). */
export function carroDoTexto(
  texto: string, base: BaseDeCarros, anterior: CarroEscolhido, opcoes: OpcoesDaLeitura = {},
): CarroEscolhido {
  const lido = entenderCarro(texto, base, opcoes);
  return {
    ...anterior,
    texto,
    montadora: lido.montadora ?? '',
    modelo: lido.modelo ?? '',
    ano: (opcoes.comAno ?? true) && lido.ano ? String(lido.ano) : '',
  };
}

// --- A palavra do modelo, as sugestões e os botões -----------------------------------

/**
 * A palavra que está sendo o MODELO — a MESMA que a leitura usa
 * (procurarModelo): a do carro reconhecido ("novo gol" → gol, "147 pick up" →
 * 147); sem carro reconhecido, a que a leitura espera da FIPE ou a primeira
 * que podia ser modelo ("vw 1975 fusc 1.6" → fusc, "1998 206" → 206). É ela
 * que a sugestão troca. `indice` conta as palavras do texto separadas por
 * espaço.
 */
export function localizarPalavra(
  texto: string, base: BaseDeCarros, opcoes: OpcoesDaLeitura = {},
): { indice: number; palavra: string } | null {
  const { montadora, resto } = separarMontadora(palavrasDe(texto ?? ''), base);
  const { k } = procurarModelo(resto, base, montadora, opcoes.anoAtual ?? new Date().getFullYear());
  const p = resto[k];
  return p ? { indice: p.indice, palavra: palavraParaBusca(p.norm) } : null;
}

/**
 * O que a tela pergunta à FIPE (consultas.familiasDoCatalogo): as palavras
 * que podem ser o modelo e que a base ainda não sabe, da esquerda para a
 * direita, até o primeiro modelo de vehicle_models — "pampa pick up" →
 * [pampa]; "weekend palio" → []; "peugeot 2008 griffe" → [2008, griffe].
 * Todas de uma vez: a leitura fica de pé com UMA ida ao banco. No máximo 3.
 */
export function palavrasParaFipe(
  texto: string, base: BaseDeCarros, opcoes: OpcoesDaLeitura = {}, max = 3,
): string[] {
  const { montadora, resto } = separarMontadora(palavrasDe(texto ?? ''), base);
  const { perguntar } = procurarModelo(
    resto, base, montadora, opcoes.anoAtual ?? new Date().getFullYear(),
  );
  return [...new Set(perguntar.map((k) => palavraParaBusca(resto[k]!.norm)))].slice(0, max);
}

/** A palavra pronta para ir num `ilike`: sem `%`, `_` e o que mais não é
 *  letra, número, ponto, hífen ou mais ("f-1000", "ka+"). */
export function palavraParaBusca(p: string): string {
  return normalizar(p).replace(/[^a-z0-9.+-]/g, '');
}

export interface SugestaoDeCarro {
  nome: string;
  montadora: string;
  fonte: 'modelo' | 'fipe';
}

/** Até 6 sugestões para a palavra do modelo: primeiro vehicle_models (por
 *  nome e apelidos: "mille" sugere Uno), depois as famílias da FIPE. Com só a
 *  montadora digitada ("fiat"), os modelos dela. */
export function sugestoesDeCarro(texto: string, base: BaseDeCarros, max = 6): SugestaoDeCarro[] {
  const { montadora } = separarMontadora(palavrasDe(texto ?? ''), base);
  const local = localizarPalavra(texto, base);
  const p = local ? chave(local.palavra) : '';
  if (!p && !montadora) return [];

  const deModelos = base.modelos
    .filter((m) => mesmaMontadora(m.montadora, montadora))
    .map((m) => {
      const chaves = [m.nome, ...m.apelidos].map(chave);
      const exato = chaves.includes(p);
      const prefixo = chaves.some((c) => c.startsWith(p));
      return { m, exato, prefixo };
    })
    .filter((x) => x.prefixo)
    .sort((a, b) => Number(b.exato) - Number(a.exato) || a.m.nome.localeCompare(b.m.nome))
    .map(({ m }): SugestaoDeCarro => ({ nome: m.nome, montadora: m.montadora, fonte: 'modelo' }));

  const jaDeModelo = (f: Familia) => base.modelos.some((m) => mesmaMontadora(m.montadora, f.montadora)
    && [m.nome, ...m.apelidos].some((a) => chave(a) === chave(f.nome)));
  const deFamilias = (p ? base.familias : [])
    .filter((f) => mesmaMontadora(f.montadora, montadora))
    .filter((f) => chave(f.nome).startsWith(p) && !jaDeModelo(f))
    .sort((a, b) => Number(chave(b.nome) === p) - Number(chave(a.nome) === p) || b.linhas - a.linhas)
    .map((f): SugestaoDeCarro => ({ nome: f.nome, montadora: f.montadora, fonte: 'fipe' }));

  return [...deModelos, ...deFamilias].slice(0, max);
}

/**
 * O texto depois de um toque numa sugestão: troca SÓ a palavra do modelo e
 * mantém o resto ("go 2010 1.0" → "Gol 2010 1.0"). Se a leitura não cair na
 * montadora da sugestão ("147" é Fiat; o toque foi no Alfa Romeo), a montadora
 * entra escrita na frente — o texto continua dizendo tudo.
 */
export function escolherSugestao(
  texto: string, base: BaseDeCarros, s: Pick<SugestaoDeCarro, 'nome' | 'montadora'>,
  opcoes: OpcoesDaLeitura = {},
): string {
  const partes = (texto ?? '').trim().split(/\s+/).filter(Boolean);
  const local = localizarPalavra(texto, base, opcoes);
  let novo: string;
  if (local && partes[local.indice] !== undefined) {
    partes[local.indice] = s.nome;
    novo = partes.join(' ');
  } else {
    novo = [...partes, s.nome].join(' ');
  }
  const lido = entenderCarro(novo, base, opcoes);
  return lido.montadora && mesmaMontadora(lido.montadora, s.montadora)
    ? novo
    : `${nomeDaMontadora(s.montadora)} ${novo}`;
}

/** "Não achei esse carro — de que montadora?": a montadora escolhida entra
 *  escrita na frente do que foi digitado, e o texto vira o modelo. */
export function comMontadora(texto: string, montadora: string): string {
  return `${nomeDaMontadora(montadora)} ${(texto ?? '').trim()}`.trim();
}

/**
 * Os carros citados no texto das peças ("LÂMPADA H4 GOL" → Gol), para os
 * botões "do texto das peças". Só vehicle_models, só palavra inteira, no
 * máximo 3, na ordem em que aparecem. Fora os apelidos ambíguos (UP, SPACE).
 * NUNCA preenche sozinho — quem decide é o toque.
 */
export function carrosNoTexto(
  textos: ReadonlyArray<string | null | undefined>, base: BaseDeCarros, max = 3,
): ModeloBase[] {
  const candidatos = base.modelos
    .flatMap((m) => [m.nome, ...m.apelidos].map((apelido) => ({
      modelo: m,
      tokens: normalizar(apelido).split(/[^a-z0-9]+/).filter(Boolean),
      chave: chave(apelido),
    })))
    .filter((c) => c.tokens.length > 0 && !AMBIGUOS_NO_TEXTO.has(c.chave))
    .sort((a, b) => b.tokens.length - a.tokens.length);

  const achados: Array<{ modelo: ModeloBase; texto: number; pos: number }> = [];
  textos.forEach((bruto, t) => {
    const tokens = normalizar(bruto ?? '').split(/[^a-z0-9]+/).filter(Boolean);
    const usado = new Array<boolean>(tokens.length).fill(false);
    for (const c of candidatos) {
      for (let i = 0; i + c.tokens.length <= tokens.length; i += 1) {
        const livre = c.tokens.every((tk, k) => !usado[i + k] && tokens[i + k] === tk);
        if (!livre) continue;
        c.tokens.forEach((_, k) => { usado[i + k] = true; });
        achados.push({ modelo: c.modelo, texto: t, pos: i });
      }
    }
  });

  const saida: ModeloBase[] = [];
  for (const a of achados.sort((x, y) => x.texto - y.texto || x.pos - y.pos)) {
    if (!saida.includes(a.modelo)) saida.push(a.modelo);
  }
  return saida.slice(0, max);
}

/**
 * As famílias a partir das linhas do catálogo FIPE (montadora, modelo):
 * primeira palavra do modelo, sem diferença de maiúscula, por montadora.
 * Palavra toda em maiúscula e só de letras ganha maiúscula inicial ("SPIN" →
 * "Spin"); "HB20" e "F-1000" ficam. Barra ou ponto no fim saem ("Strada/").
 */
export function familiasDe(
  linhas: ReadonlyArray<{ montadora: string; modelo: string }>,
): Familia[] {
  const porChave = new Map<string, Familia & { _segundas: Set<string> }>();
  for (const l of linhas) {
    const palavras = (l.modelo ?? '').trim().split(/\s+/);
    const primeira = (palavras[0] ?? '').replace(/[/.]+$/, '');
    if (!chave(primeira) || !l.montadora) continue;
    const k = `${l.montadora.toLowerCase()}|${chave(primeira)}`;
    let f = porChave.get(k);
    if (!f) {
      const nome = /^\p{Lu}+$/u.test(primeira) && primeira.length > 1
        ? primeira.charAt(0) + primeira.slice(1).toLowerCase()
        : primeira;
      f = { montadora: l.montadora, nome, linhas: 0, segundas: [], _segundas: new Set() };
      porChave.set(k, f);
    }
    f.linhas += 1;
    const segunda = palavras[1] ? chave(palavras[1]) : '';
    if (segunda) f._segundas.add(segunda);
  }
  return [...porChave.values()].map(({ _segundas, ...f }) => ({ ...f, segundas: [..._segundas] }));
}
